import React, { useState, useEffect } from "react";
import {
  Breadcrumb,
  Button,
  Card,
  Col,
  Divider,
  Empty,
  Layout,
  Modal,
  Result,
  Row,
  Skeleton,
  Statistic,
  Table,
  Tag,
  notification,
  theme,
  Descriptions,
} from "antd";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowLeftOutlined,
  UserOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  CreditCardOutlined,
  CalendarOutlined,
  EyeOutlined,
  StopOutlined,
} from "@ant-design/icons";
import {
  apiSubscriberDetail,
  apiSubscriptionPaymentsList,
  apiSubscriptionPaymentDetail,
  apiAdminSubscriberCancel,
} from "../../../../api/subscription";
import { formatDate, formatDateTime, formatPhoneWithCountry, formatPrice } from "../../../../utils/format";
import {
  isPaymentPayable,
  paymentMethodLabel,
  paymentStatusMeta,
  subscriberStatusMeta,
} from "../../../../utils/subscriptions";
import "./SubscriberDetail.scss";

const { Content } = Layout;

const SUBSCRIBER_STATUS_ICONS = {
  paid: CheckCircleOutlined,
  pending: ClockCircleOutlined,
  overdue: ExclamationCircleOutlined,
  canceled: CloseCircleOutlined,
  failed: CloseCircleOutlined,
};

const SubscriberDetail = () => {
  const { id: planId, subscriberId } = useParams();
  const navigate = useNavigate();
  const [subscriber, setSubscriber] = useState(null);
  const [payments, setPayments] = useState([]);
  const [isDetailsFetching, setIsDetailsFetching] = useState(false);
  const [isPaymentsFetching, setIsPaymentsFetching] = useState(false);
  const [activeError, setActiveError] = useState(false);
  const [errorStatus, setErrorStatus] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    total: 0,
    pageSize: 10,
  });

  // Payment detail modal
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [paymentDetailLoading, setPaymentDetailLoading] = useState(false);

  // Cancel subscriber modal
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  useEffect(() => {
    if (planId && subscriberId) {
      fetchSubscriberDetails();
      fetchPayments({ page: 1 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId, subscriberId]);

  const fetchSubscriberDetails = async () => {
    setIsDetailsFetching(true);
    try {
      const data = await apiSubscriberDetail(planId, subscriberId);
      setSubscriber(data);
    } catch (error) {
      setActiveError(true);
      setErrorStatus(error.status);
      notification.error({
        message: "Erro",
        description: "Ocorreu um erro ao buscar os detalhes do assinante.",
        placement: "topRight",
      });
    } finally {
      setIsDetailsFetching(false);
    }
  };

  const fetchPayments = async (params = {}) => {
    setIsPaymentsFetching(true);
    try {
      const filters = { page: params.page || 1 };
      const data = await apiSubscriptionPaymentsList(planId, subscriberId, filters);
      setPayments(data.results || data || []);
      setPagination((prev) => ({
        ...prev,
        current: params.page || 1,
        total: data.count || (Array.isArray(data) ? data.length : 0),
      }));
    } catch (error) {
      notification.error({
        message: "Erro",
        description: "Ocorreu um erro ao carregar os pagamentos.",
        placement: "topRight",
      });
    } finally {
      setIsPaymentsFetching(false);
    }
  };

  const handlePaymentsTableChange = (paginationParams) => {
    fetchPayments({ page: paginationParams.current });
  };

  const handleViewPayment = async (payment) => {
    setPaymentModalVisible(true);
    setPaymentDetailLoading(true);
    try {
      const data = await apiSubscriptionPaymentDetail(planId, subscriberId, payment.id);
      setSelectedPayment(data);
    } catch (error) {
      notification.error({
        message: "Erro",
        description: "Ocorreu um erro ao carregar os detalhes do pagamento.",
        placement: "topRight",
      });
      setPaymentModalVisible(false);
    } finally {
      setPaymentDetailLoading(false);
    }
  };

  const handleCancelSubscriber = async () => {
    setCancelLoading(true);
    try {
      const result = await apiAdminSubscriberCancel(planId, subscriberId);
      setSubscriber(result.subscriber || { ...subscriber, status: "canceled" });
      setCancelModalVisible(false);
      notification.success({
        message: "Sucesso",
        description: "Assinatura cancelada com sucesso.",
        placement: "topRight",
      });
    } catch (error) {
      notification.error({
        message: "Erro",
        description: error.message || "Ocorreu um erro ao cancelar a assinatura.",
        placement: "topRight",
      });
    } finally {
      setCancelLoading(false);
    }
  };

  const getStatusTag = (status) => {
    const { color, label } = subscriberStatusMeta(status);
    const StatusIcon = SUBSCRIBER_STATUS_ICONS[status];
    return (
      <Tag color={color} icon={StatusIcon ? <StatusIcon /> : undefined}>
        {label}
      </Tag>
    );
  };

  const getPaymentStatusTag = (status) => {
    const { color, label } = paymentStatusMeta(status);
    return <Tag color={color}>{label}</Tag>;
  };

  const paymentsColumns = [
    {
      title: "Data de Vencimento",
      dataIndex: "due_date",
      key: "due_date",
      render: (date) => formatDate(date),
    },
    {
      title: "Valor",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => formatPrice(parseFloat(amount)),
    },
    {
      title: "Metodo",
      dataIndex: "payment_method",
      key: "payment_method",
      responsive: ["md"],
      render: paymentMethodLabel,
    },
    {
      title: "Status",
      dataIndex: "payment_status",
      key: "payment_status",
      render: getPaymentStatusTag,
    },
    {
      title: "Pago em",
      dataIndex: "paid_at",
      key: "paid_at",
      responsive: ["lg"],
      render: (date) => (date ? formatDateTime(date) : "-"),
    },
    {
      title: "Acoes",
      key: "actions",
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewPayment(record)}
        >
          Ver
        </Button>
      ),
    },
  ];

  const renderError = () => {
    if (activeError) {
      return (
        <Result
          status={errorStatus === "500" ? "500" : "404"}
          title={errorStatus}
          subTitle={
            errorStatus === "500"
              ? "Ocorreu um erro no servidor, tente novamente mais tarde."
              : "Assinante nao encontrado."
          }
          extra={
            <Button onClick={() => navigate(`/assinatura/${planId}`)} type="primary">
              Voltar para Assinatura
            </Button>
          }
        />
      );
    }
  };

  const renderMetrics = () => {
    if (!subscriber?.metrics) return null;

    const metricsConfig = [
      {
        title: "Total de Pagamentos",
        value: subscriber.metrics.total_payments,
        icon: <DollarOutlined />,
        color: "#1890ff",
      },
      {
        title: "Pagos",
        value: subscriber.metrics.paid_payments,
        icon: <CheckCircleOutlined />,
        color: "#52c41a",
      },
      {
        title: "Pendentes",
        value: subscriber.metrics.pending_payments,
        icon: <ClockCircleOutlined />,
        color: "#faad14",
      },
      {
        title: "Em Atraso",
        value: subscriber.metrics.overdue_payments,
        icon: <ExclamationCircleOutlined />,
        color: "#fa8c16",
      },
      {
        title: "Falhos",
        value: subscriber.metrics.failed_payments,
        icon: <CloseCircleOutlined />,
        color: "#f5222d",
      },
    ];

    return (
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        {metricsConfig.map((metric, index) => (
          <Col xs={12} sm={8} md={4} key={index}>
            <Card className="metric-card" style={{ borderTop: `3px solid ${metric.color}` }}>
              <Statistic
                title={
                  <span className="metric-title">
                    {React.cloneElement(metric.icon, { style: { color: metric.color, marginRight: 4 } })}
                    {metric.title}
                  </span>
                }
                value={metric.value}
                valueStyle={{ color: metric.color, fontSize: 24 }}
              />
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  return (
    <div className="SubscriberDetail">
      <Layout style={{ padding: "1.25rem" }}>
        <Breadcrumb style={{ margin: "16px 0" }}>
          <div
            style={{
              display: "flex",
              width: "100%",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h2 className="title-page">Detalhes do Assinante</h2>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(`/assinatura/${planId}`)}
            >
              Voltar
            </Button>
          </div>
          <Divider />
          <Breadcrumb.Item>
            <Link to="/assinaturas">Assinaturas</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to={`/assinatura/${planId}`}>Detalhes do Plano</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>Assinante</Breadcrumb.Item>
        </Breadcrumb>

        <Content
          style={{
            padding: 24,
            margin: 0,
            minHeight: 280,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
          }}
        >
          {isDetailsFetching ? (
            <Skeleton active />
          ) : activeError ? (
            renderError()
          ) : (
            <>
              {/* Subscriber Info */}
              <div className="container-subtitle">
                <h3 className="subtitle-page">
                  <UserOutlined style={{ marginRight: 8 }} />
                  Informacoes do Assinante
                </h3>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  {subscriber && getStatusTag(subscriber.status)}
                  {subscriber && subscriber.status !== "canceled" && (
                    <Button
                      type="primary"
                      danger
                      icon={<StopOutlined />}
                      onClick={() => setCancelModalVisible(true)}
                    >
                      Cancelar Assinatura
                    </Button>
                  )}
                </div>
              </div>
              <Divider />

              <Row gutter={[24, 24]}>
                <Col xs={24} md={12}>
                  <Card>
                    <Descriptions column={1} size="small">
                      <Descriptions.Item label="Nome">
                        {subscriber?.name || "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Email">
                        {subscriber?.email || "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Telefone">
                        {subscriber?.phone_number
                          ? formatPhoneWithCountry(subscriber?.country_code, subscriber?.phone_number)
                          : "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label="CPF/CNPJ">
                        {subscriber?.taxpayer_id || "-"}
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Col>
                <Col xs={24} md={12}>
                  <Card>
                    <Descriptions column={1} size="small">
                      <Descriptions.Item label="Assinante desde">
                        {subscriber?.subscribed_at ? formatDate(subscriber.subscribed_at) : "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Proxima Cobranca">
                        {subscriber?.next_billing_date ? formatDate(subscriber.next_billing_date) : "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Ultimo Pagamento">
                        {subscriber?.last_payment_date ? formatDate(subscriber.last_payment_date) : "-"}
                      </Descriptions.Item>
                      {subscriber?.canceled_at && (
                        <Descriptions.Item label="Cancelado em">
                          {formatDate(subscriber.canceled_at)}
                        </Descriptions.Item>
                      )}
                      <Descriptions.Item label="Cartao Salvo">
                        {subscriber?.saved_card ? (
                          <Tag icon={<CreditCardOutlined />} color="blue">
                            {subscriber.saved_card.card_brand} **** {subscriber.saved_card.last4_digits} — {subscriber.saved_card.holder_name}
                          </Tag>
                        ) : (
                          "-"
                        )}
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Col>
              </Row>

              {/* Metrics */}
              {renderMetrics()}
            </>
          )}
        </Content>

        {/* Payments Section */}
        <Content
          style={{
            padding: 24,
            margin: "30px 0 0 0",
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
          }}
        >
          <div className="container-subtitle">
            <h3 className="subtitle-page">
              <CreditCardOutlined style={{ marginRight: 8 }} />
              Pagamentos
            </h3>
          </div>
          <Divider />
          {isPaymentsFetching ? (
            <Skeleton active />
          ) : (
            <Table
              columns={paymentsColumns}
              dataSource={payments.map((item) => ({ ...item, key: item.id }))}
              pagination={{
                current: pagination.current,
                total: pagination.total,
                pageSize: pagination.pageSize,
                showSizeChanger: false,
              }}
              onChange={handlePaymentsTableChange}
              locale={{ emptyText: <Empty description="Nenhum pagamento encontrado" /> }}
              bordered
            />
          )}
        </Content>
      </Layout>

      {/* Payment Detail Modal */}
      <Modal
        title="Detalhes do Pagamento"
        open={paymentModalVisible}
        onCancel={() => {
          setPaymentModalVisible(false);
          setSelectedPayment(null);
        }}
        footer={[
          selectedPayment?.transaction && (
            <Button
              key="transaction"
              type="primary"
              onClick={() => navigate(`/transacao/${selectedPayment.transaction}`)}
            >
              Ver Transacao
            </Button>
          ),
          <Button key="close" onClick={() => setPaymentModalVisible(false)}>
            Fechar
          </Button>,
        ]}
        width={600}
      >
        {paymentDetailLoading ? (
          <Skeleton active />
        ) : selectedPayment ? (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="ID do Pagamento">
              {selectedPayment.id}
            </Descriptions.Item>
            <Descriptions.Item label="Valor">
              {formatPrice(parseFloat(selectedPayment.amount))}
            </Descriptions.Item>
            <Descriptions.Item label="Metodo de Pagamento">
              {paymentMethodLabel(selectedPayment.payment_method)}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              {getPaymentStatusTag(selectedPayment.payment_status)}
            </Descriptions.Item>
            <Descriptions.Item label="Data de Vencimento">
              {formatDate(selectedPayment.due_date)}
            </Descriptions.Item>
            <Descriptions.Item label="Pago em">
              {selectedPayment.paid_at ? formatDateTime(selectedPayment.paid_at) : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Criado em">
              {formatDateTime(selectedPayment.created_at)}
            </Descriptions.Item>
            {selectedPayment.transaction && (
              <Descriptions.Item label="ID da Transacao">
                <Link to={`/transacao/${selectedPayment.transaction}`}>
                  {selectedPayment.transaction}
                </Link>
              </Descriptions.Item>
            )}
            {isPaymentPayable(selectedPayment.payment_status, subscriber?.status) && (
              <Descriptions.Item label="Link de Pagamento">
                <a
                  href={`/assinar/${planId}/${subscriberId}/pagar/${selectedPayment.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#1890ff" }}
                >
                  {`${window.location.origin}/assinar/${planId}/${subscriberId}/pagar/${selectedPayment.id}`}
                </a>
              </Descriptions.Item>
            )}
          </Descriptions>
        ) : null}
      </Modal>

      {/* Cancel Subscriber Modal */}
      <Modal
        title="Cancelar Assinatura"
        open={cancelModalVisible}
        onCancel={() => setCancelModalVisible(false)}
        footer={[
          <Button key="back" onClick={() => setCancelModalVisible(false)}>
            Voltar
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger
            loading={cancelLoading}
            onClick={handleCancelSubscriber}
          >
            Confirmar Cancelamento
          </Button>,
        ]}
      >
        <p>Tem certeza que deseja cancelar a assinatura de <strong>{subscriber?.name}</strong>?</p>
        <p>Esta acao nao pode ser desfeita e o assinante perdera acesso aos beneficios do plano.</p>
      </Modal>
    </div>
  );
};

export default SubscriberDetail;
